'use client';

import { useState, useCallback } from 'react';

// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// hooks

// utils

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

// assets

// components
import Iconify from 'src/components/iconify';

import { useTranslate } from '../../../../locales';
import { useSettingsContext } from '../../../../components/settings';
import { ComponentTypeProps } from '../../../../types/page-generator';
import { FamilyTab } from './components/personal-information/family-tab';
import { InterestsTab } from './components/personal-information/interests-tab';
import { PersonalInformationTab } from './components/personal-information/personal-information-tab';

// ----------------------------------------------------------------------

type AccountTab = {
  value: string;
  label: string;
};

export default function PersonalInformation({ block }: ComponentTypeProps) {
  const { t } = useTranslate();
  const settings = useSettingsContext();
  const [activeAction, setActiveAction] = useState('');
  const [currentTab, setCurrentTab] = useState('personal');
  const quickActions = [
    'הנחה בארנונה',
    'עדכון סיסמה',
    'אתר רשות עירוני',
    'הרשמה לניוזלטר',
    'חילוף מחזיקים בנכס',
    'זימון תור',
  ];
  const TABS: AccountTab[] = [
    {
      value: 'personal',
      label: 'Personal information',
    },
    {
      value: 'family',
      label: 'Family ties',
    },
    {
      value: 'interests',
      label: 'Interests',
    },
  ];

  const personalInformationTabItem = Object.prototype.hasOwnProperty.call(
    block,
    'personalInformationTabItem'
  )
    ? block?.personalInformationTabItem
    : true;
  const familyTabItem = Object.prototype.hasOwnProperty.call(block, 'familyTabItem')
    ? block?.familyTabItem
    : true;
  const interestsTabItem = Object.prototype.hasOwnProperty.call(block, 'personalInterestsTabItem')
    ? block?.personalInterestsTabItem
    : true;

  const activeTabNames: string[] = [];

  if (personalInformationTabItem) activeTabNames.push('personal');
  if (familyTabItem) activeTabNames.push('family');
  if (interestsTabItem) activeTabNames.push('interests');

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  return (
    <Card sx={{ borderRadius: 1 }}>
      <Stack width={1} gap={2}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Box
            sx={{
              background: 'rgb(0, 89, 210)',
              borderRadius: 1,
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify icon="prime:user" width={30} color="white" />
          </Box>
          <Typography
            sx={{ fontSize: '24px', fontWeight: 1000 }}
          >{`${t('Personal information')}`}</Typography>
        </Stack>
        <Stack
          sx={{
            borderRadius: '5px',
            border: '1px solid',
            borderColor: '#d9d9d9',
            padding: '16px',
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 1000 }}>פעולות מהירות</Typography>
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            {quickActions.map((item: string, id) => (
              <Stack
                key={id}
                direction="row"
                gap={1}
                alignItems="center"
                sx={{
                  borderLeft: activeAction === item && id !== 0 ? '1px solid blue' : 'none',
                  borderRight:
                    activeAction === item && id !== quickActions.length - 1
                      ? '1px solid blue'
                      : 'none',
                }}
              >
                <Iconify icon="iconoir:page" width={10} color="#282894" />
                <Typography
                  onClick={() => setActiveAction(item)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { color: 'blue' },
                  }}
                >
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Tabs value={currentTab} onChange={handleChangeTab}>
          {TABS.filter((tab: AccountTab) => activeTabNames.includes(tab.value)).map(
            (tab: AccountTab) => (
              <Tab key={tab.value} label={`${t(`${tab.label}`)}`} value={tab.value} />
            )
          )}
        </Tabs>

        {currentTab === 'personal' && personalInformationTabItem && (
          <PersonalInformationTab block={block} />
        )}

        {currentTab === 'family' && familyTabItem && <FamilyTab block={block} />}

        {currentTab === 'interests' && interestsTabItem && <InterestsTab block={block} />}
      </Stack>
    </Card>
  );
}
